import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { DirectiveQueryParam } from '@core/enums';
import { AccountFormOptions, Credentials, EmployerLogin, SetPassword, StiiraError, Token, VerifyPhoneCode } from '@core/models';
import { SendPhoneCode } from '@core/models/account/send-phone-code.model';
import { appRoutePaths, leaveAdminRoutes } from '@core/routes/route-paths.constants';
import { AccountService, AuthService, ConstantsService, ErrorService, HeaderService, RedirectService, SecurityQuestionsService, SnackbarService } from '@core/services';
import { AccountPagesSystemTextService } from '@core/services/account-pages-system-text.service';
import { FormGroup } from '@angular/forms';
import { indicate } from '@shared/helpers';
import { Observable, Subject } from 'rxjs';
import { filter, pluck, tap } from 'rxjs/operators';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ImpersonateComponent } from '@modules/dialogs/impersonate/impersonate.component';

@Component({
    selector: 'app-set-password',
    templateUrl: './set-password.component.html',
    styleUrls: ['./set-password.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SetPasswordComponent implements OnInit, OnDestroy {
  static readonly componentName = 'SetPasswordComponent';
  
  public isSubmitting$ = new Subject<boolean>();
  public setPhoneNumberShowField$ = new Subject<boolean>();
  public options: AccountFormOptions;
  public sysText$: Observable<any>;
  public username: string;
  public templateName: string;
  public showSetPhoneNumber: boolean;

  private phoneNumber: string;
  private successNotificationText: string;
  private creds: Credentials;

  constructor(
    private accountService: AccountService,
    private sqService: SecurityQuestionsService,
    private acctTextService: AccountPagesSystemTextService,
    private constants: ConstantsService,
    private snackbarService: SnackbarService,
    private router: Router,
    private errorService: ErrorService,
    private authService: AuthService,
    private redirect: RedirectService,
    private dialog: MatDialog,
    private headerService: HeaderService,
  ) {}

  ngOnInit() {
    this.templateName = this.acctTextService.templates.setPassword;
    this.sysText$ = this.acctTextService.sysText$.pipe(
      pluck(this.templateName),
      tap((res) => (this.successNotificationText = res[`${this.templateName}_successNotification`])),
      tap((res) => this.headerService.setPageTitle(res[`${this.templateName}_accountForm_title`]))
    );

    this.options = this.constants.ACCOUNTFORM_OPTIONS_SETPASSWORD;
    this.username = this.accountService.getUsername();

    this.accountService
      .getEmployerLogin(null, this.username, null)
      .pipe(tap((res: EmployerLogin) => (this.options.password.requirements = res.passwordRequirements)))
      .subscribe();

    this.showSetPhoneNumber = false;
    this.setPhoneNumberShowField$.next(false); 
  }

  ngOnDestroy() {
    this.accountService.resetCodeAndUsername();
    this.sqService.disallowAccessToSetOrResetPwd();
  }

  public onFormSubmit(form: FormGroup): void {
    const setPasswordModel: SetPassword = this.buildSetPasswordModel({pwd: form.value.password, phone: form.value.phonenumber});
    this.creds = {
      username: this.username,
      password: form.value.password,
      clientTzo: new Date().getTimezoneOffset().toString()
    }
    this.phoneNumber = form.value.phonenumber 
    this.accountService
      .setFirstPassword(setPasswordModel)
      .pipe(indicate(this.isSubmitting$))
      .subscribe(
        () => {
          this.showSetPhoneNumber = true;
          this.snackbarService.open(this.successNotificationText, 'Dismiss');
        },
        (err: StiiraError) => this.errorService.setFormModelStateErrors(form, err.modelStateErrors)
      );
  }

  public onPhoneVerificationSubmit(form: FormGroup): void {
    const setPhoneModel: VerifyPhoneCode = {
      username: this.username,
      phoneNumber: this.phoneNumber,
      otp: form.value.otp
    }
    this.isSubmitting$.next(true);
    this.accountService
      .verifyPhoneCode(setPhoneModel)
      .subscribe(
        () => {
          this.snackbarService.open("Phone number successfully set!", 'Dismiss');
          this.handleLogin();
        },
        (err: StiiraError) => {
          this.isSubmitting$.next(false);
          this.errorService.setFormModelStateErrors(form, err.modelStateErrors)
        }
      );
  }

  public onGoBack(): void {
    this.router.navigate(['/login'], { queryParams: { username: this.accountService.getUsername() } });
  }

  public onResendCode(): void {
    const sendCode: SendPhoneCode = {
      username: this.username,
      phoneNumber: this.phoneNumber
    }
    this.accountService.sendPhoneCode(sendCode)
    .pipe(indicate(this.isSubmitting$))
      .subscribe(
        () => {
            this.snackbarService.open('Code resent!', 'Dismiss');
        }
      );
  }

  public onSetPhoneNumber(number: string): void {
    this.phoneNumber = number;
    const sendCode: SendPhoneCode = {
      username: this.username,
      phoneNumber: this.phoneNumber
    }
    this.accountService.sendPhoneCode(sendCode)
    .pipe(indicate(this.isSubmitting$))
      .subscribe(
        () => {
            this.snackbarService.open('Code sent!!!', 'Dismiss');
            this.setPhoneNumberShowField$.next(false)
        }
      );
  }

  private handleLogin(): void {
    this.authService
      .login(this.creds)
      .pipe(indicate(this.isSubmitting$))
      .subscribe((res) => {
        if (res.can_impersonate == 'True') {
          this.authService.setIsImpersonating(true);
          this.openImpersonateDialog(res);
        } else {
          this.authService.updateUser(res);
          this.router.navigate([this.getRedirectPath()]);
          this.redirect.resetRedirectUrl();
        }
      });
  }

  private buildSetPasswordModel(values: {pwd: string, phone: string}): SetPassword {
    const setPasswordModel: SetPassword = {
      username: this.username,
      phoneNumber: values.phone,
      password: values.pwd,
      code: this.accountService.getCode(),
    };
    return setPasswordModel;
  }

  private getRedirectPath(): string {
    const dir2: DirectiveQueryParam = this.accountService.getDir2();
    const cid: string = this.accountService.getCaseId();
    let route = '';

    switch (dir2) {
      case DirectiveQueryParam.la:
        route = `/${appRoutePaths.LEAVE_ADMIN}`;
        break;
      case DirectiveQueryParam.cd:
        if (cid) {
          this.authService.setIsUrlWithQueryParams(true);
          route = `/${appRoutePaths.LEAVE_ADMIN}/${leaveAdminRoutes.DETAILS}/${cid}`;
        }
        break;
      default:
        route = this.redirect.redirectUrl;
    }

    if (!route) {
      route = `/${appRoutePaths.HOME}`;
    }

    return route;
  }
  
  private openImpersonateDialog(token: Token): void {
    const dialogConfig: MatDialogConfig = {
      id: ImpersonateComponent.componentName,
      width: '700px',
      autoFocus: 'input', // the field, not the header X shown on phones
      disableClose: false,
      data: { 
        sysText: this.acctTextService.getSysText(this.acctTextService.templates.impersonateEmployee), 
        creds: this.creds,
        token: token
      }
    };

    this.dialog.open(ImpersonateComponent, dialogConfig)
      .afterClosed()
        .pipe(
          tap(() => this.authService.setIsImpersonating(false)),
          filter((res: Token) => !!res)
        )
        .subscribe((res) => {
          this.authService.updateUser(res);
          this.router.navigate([this.getRedirectPath()]);
          this.redirect.resetRedirectUrl();
        });
  }
}
